import { forwardRef } from "react";

import ToplistIcon from "@/assets/icons/bolt.svg";
import FireIcon from "@/assets/icons/fire.svg";
import PositiveImpactIcon from "@/assets/icons/heart.svg";
import RocketLaunchIcon from "@/assets/icons/rocket-launch.svg";
import NegativeImpactIcon from "@/assets/icons/shield-exclamation.svg";
import RecordIcon from "@/assets/icons/trophy.svg";
import { INSIGHT_TYPE, InsightType } from "@/constants";
import { cx } from "@/utils";
import Tooltip from "@components/Tooltip";
import { i18n } from "@lingui/core";

import styles from "./InsightBadge.module.css";

type BadgeProps = {
  icon: React.FunctionComponent<React.SVGProps<SVGSVGElement>>;
  className?: string;
  children?: React.ReactNode;
  innerRef?: React.Ref<HTMLDivElement>;
  size?: "sm" | "md";
} & Omit<React.HTMLProps<HTMLDivElement>, "size">;

// Badge component
export const Badge = ({
  icon,
  className,
  children,
  size = "md",
  innerRef,
  ...props
}: BadgeProps) => {
  const Icon = icon;
  return (
    <div className={cx(styles.root, className)} ref={innerRef} {...props}>
      <div
        className={cx(styles.size, {
          [styles.sm]: size === "sm",
          [styles.md]: size === "md",
        })}
      >
        <Icon className={styles.icon} />
      </div>
      {children && <span className={styles.content}>{children}</span>}
    </div>
  );
};

// Helper function to create a badge component with a given icon and class name
const WithBadge = (
  icon: React.FunctionComponent<React.SVGProps<SVGSVGElement>>,
  className: string
) => {
  const BadgeInner = (props: Omit<BadgeProps, "icon">) => (
    <Badge icon={icon} {...props} className={cx(className, props.className)} />
  );
  return BadgeInner;
};

// Create the badge components
const HighReachBadge = WithBadge(FireIcon, styles.yellow);
const ToplistBadge = WithBadge(ToplistIcon, styles.pink);
const RecordBadge = WithBadge(RecordIcon, styles.purple);
const IncreaseBadge = WithBadge(RocketLaunchIcon, styles.blue);
const PositiveImpactBadge = WithBadge(PositiveImpactIcon, styles.green);
const NegativeImpactBadge = WithBadge(NegativeImpactIcon, styles.red);

// Map the insight type to the badge component
const BADGES = {
  [INSIGHT_TYPE.HIGH_REACH_MENTION]: HighReachBadge,
  [INSIGHT_TYPE.MOST_MENTIONS_FOR_CHANNEL_TYPE]: RecordBadge,
  [INSIGHT_TYPE.MORE_MENTIONS_THAN_USUAL]: IncreaseBadge,
  [INSIGHT_TYPE.TOPLIST_MENTIONS]: ToplistBadge,
  [INSIGHT_TYPE.NEGATIVE_IMPACT]: NegativeImpactBadge,
  [INSIGHT_TYPE.POSITIVE_IMPACT]: PositiveImpactBadge,
  [INSIGHT_TYPE.MORE_CHANNELS_THAN_USUAL]: IncreaseBadge,
  [INSIGHT_TYPE.MOST_MENTIONS]: RecordBadge,
};

export const SETTINGS: Partial<
  Record<InsightType, { label: string; tooltip: string }>
> = {
  [INSIGHT_TYPE.HIGH_REACH_MENTION]: {
    label: L.HIGH_REACH_MENTION_BADGE,
    tooltip: L.HIGH_REACH_MENTION_BADGE_TOOLTIP,
  },
  [INSIGHT_TYPE.TOPLIST_MENTIONS]: {
    label: L.TOPLIST_MENTIONS_BADGE,
    tooltip: L.TOPLIST_MENTIONS_BADGE_TOOLTIP,
  },
  [INSIGHT_TYPE.NEGATIVE_IMPACT]: {
    label: L.NEGATIVE_IMPACT_MENTIONS_BADGE,
    tooltip: L.NEGATIVE_IMPACT_MENTIONS_BADGE_TOOLTIP,
  },
  [INSIGHT_TYPE.POSITIVE_IMPACT]: {
    label: L.POSITIVE_IMPACT_MENTIONS_BADGE,
    tooltip: L.POSITIVE_IMPACT_MENTIONS_BADGE_TOOLTIP,
  },
  [INSIGHT_TYPE.MOST_MENTIONS]: {
    label: L.NEW_RECORD_BADGE,
    tooltip: L.NEW_RECORD_BADGE_TOOLTIP,
  },
  [INSIGHT_TYPE.MORE_MENTIONS_THAN_USUAL]: {
    label: L.MOST_MENTIONS_BADGE,
    tooltip: L.MOST_MENTIONS_BADGE_TOOLTIP,
  },
  [INSIGHT_TYPE.MORE_CHANNELS_THAN_USUAL]: {
    label: L.MOST_SOURCE_BADGE,
    tooltip: L.MOST_SOURCE_BADGE_TOOLTIP,
  },
  [INSIGHT_TYPE.MOST_MENTIONS_FOR_CHANNEL_TYPE]: {
    label: L.NEW_RECORD_BADGE,
    tooltip: L.NEW_RECORD_BADGE_TOOLTIP,
  },
};

type InsightProps = { type: InsightType; tooltip?: boolean } & Omit<
  BadgeProps,
  "icon" | "innerRef"
>;

// Helper component to render a badge for a given insight type
const InsightBadge = forwardRef<HTMLDivElement, InsightProps>(
  ({ type, tooltip = true, ...props }, ref) => {
    const Badge = BADGES[type] || null;
    if (!Badge) return null;
    const settings = SETTINGS[type];
    if (!tooltip)
      return (
        <Badge
          innerRef={ref}
          {...props}
          label={i18n._(settings?.label || "")}
        />
      );
    return (
      <Tooltip asChild={false} content={i18n._(settings?.tooltip || "")}>
        <Badge
          innerRef={ref}
          {...props}
          label={i18n._(settings?.label || "")}
        />
      </Tooltip>
    );
  }
);

InsightBadge.displayName = "InsightBadge";

export default InsightBadge;
